import React, { useContext } from 'react';
import moment from 'moment-timezone';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import { AccountContext } from '../../../../components/AccountContext';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import { growlErrors } from '../../../../utils/NotificationUtil';
import { defineMessages, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { AppConstants } from '../../../../shared/Constants';
import { UPDATE_SUPER_PROFILE_STATUS_MUTATION } from '../services/super--profiles.service';
import useSubscriptionExpiredRestrictions from '../../../../hooks/useSubscriptionExpiredRestrictions';

const i18nMessages = defineMessages({
  active: {
    id: 'lbl.active',
    defaultMessage: 'Active',
  },
  inactive: {
    id: 'lbl.inactive',
    defaultMessage: 'Inactive',
  },
  activate: {
    id: 'account.super-profiles.actions.activate',
    defaultMessage: 'Activate',
  },
  deactivate: {
    id: 'account.super-profiles.actions.deactivate',
    defaultMessage: 'Deactivate',
  },
  edit: { id: 'actions.edit', defaultMessage: 'Edit' },
  delete: { id: 'actions.delete', defaultMessage: 'Delete' },
});

const SuperProfilesListEntry = ({ superProfile, setSuperProfileToDelete }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();
  const history = useHistory();

  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const [updateSuperProfileStatusMutation, { loading }] = useMutation(UPDATE_SUPER_PROFILE_STATUS_MUTATION, {
    refetchQueries: ['superProfilesQuery'],
  });

  const isActive = superProfile.active;

  const icon = loading ? 'pi pi-spin pi-spinner' : isActive ? 'pi pi-times' : 'pi pi-check';

  const className = loading ? 'p-button-secondary' : isActive ? 'p-button-danger' : 'p-button-success';

  const tooltip = loading
    ? ''
    : isActive
    ? formatMessage(i18nMessages.deactivate)
    : formatMessage(i18nMessages.activate);

  const updateSuperProfileStatus = async (superProfileId, isActive) => {
    try {
      await updateSuperProfileStatusMutation({
        variables: {
          input: {
            accountId: currentAccount,
            superProfileId,
            active: !isActive,
          },
        },
      });
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  return (
    <div key={superProfile.id} className="flex-space-between margin-bottom-1">
      <div>
        <Link to={`/accounts/${currentAccount}/super-profiles/${superProfile.id}`} className="app-link">
          {superProfile.internalName}
        </Link>
      </div>
      <div>{moment(superProfile.createdOn).format(AppConstants.DATE_INPUT_FORMAT)}</div>
      <div>{superProfile.updatedOn ? moment(superProfile.updatedOn).format(AppConstants.DATE_INPUT_FORMAT) : '-'}</div>
      <div>{superProfile.publicTitle}</div>
      <div>{superProfile.publicDescription}</div>
      <div>{superProfile.jobProfile.name}</div>
      <div>{superProfile.jobProfile.assessmentTemplate.title}</div>
      <div>{isActive ? formatMessage(i18nMessages.active) : formatMessage(i18nMessages.inactive)}</div>
      <div className="flex-space-center">
        <Button
          icon={icon}
          className={className}
          tooltip={tooltip}
          disabled={subscriptionIsExpired}
          onClick={() => updateSuperProfileStatus(superProfile.id, isActive)}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-secondary"
          tooltip={formatMessage(i18nMessages.edit)}
          disabled={subscriptionIsExpired}
          onClick={() => history.push(`/accounts/${currentAccount}/super-profiles/${superProfile.id}`)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-secondary"
          tooltip={formatMessage(i18nMessages.delete)}
          disabled={subscriptionIsExpired}
          onClick={() => setSuperProfileToDelete(superProfile.id)}
        />
      </div>
    </div>
  );
};

export default SuperProfilesListEntry;
